// Lib
import { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { isAfter } from 'date-fns';

// Actions
import { ApplyFilters, UpdateProductsPage } from '../../../actions/products';
import {
  ChangeFilter,
  ClearFilters,
  SaveFilters,
  SetFilters,
} from '../../../actions/filters';

// Constants
import { SEARCHABLE_FIELDS } from '../../../constants/products';

// Utils
import multiSearch from '../../../utils/multiSearch';
import { separatePrescribedProducts } from '../../../utils/prescribingInfo';
import applyProductFilters from './applyProductFilters';
import {
  createNameComparator,
  createDateComparator,
  createNumberComparator,
  createOrdersComparator,
} from './sortComparators';
import {
  getDependentFilterOptions as getDependentFilterOptionsPure,
  getDependentFilters as getDependentFiltersPure,
} from './dependentFilters';

// Actions
import { SetLoading } from '../../../actions/loading';

const APPLY_FILTERS_DELAY_MS = 500;

const useFilters = () => {
  const dispatch = useDispatch();
  const [initialFiltersApplied, setInitialFiltersApplied] = useState(false);
  const { products, searchValue, filters, isLoading } = useSelector(
    (state) => ({
      products: state.products,
      searchValue: state.search,
      filters: state.filters,
      isLoading: state.isLoading,
    }),
  );

  const applySearch = useCallback(
    (product) => {
      if (!searchValue) {
        return true;
      }
      if (searchValue) {
        const field = SEARCHABLE_FIELDS.find((field) => {
          return multiSearch(product[field], searchValue.split(' '));
        });
        if (field) {
          return true;
        }
      }
    },
    [searchValue],
  );

  useEffect(() => {
    if (filters.loaded) {
      const changedFilters = { ...filters };
      delete changedFilters.loaded;
      dispatch(SaveFilters(changedFilters));
    }
  }, [dispatch, filters]);

  const applyFilters = useCallback(
    (product) =>
      applyProductFilters(product, filters, products.frequentlyOrdered),
    [filters, products.frequentlyOrdered],
  );

  const sortByName = useCallback(
    (order) =>
      createNameComparator(
        order,
        products.all.obj,
        filters.sorting.orderBy.toLowerCase(),
      ),
    [filters, products.all.obj],
  );

  const sortByDate = useCallback(
    (order) =>
      createDateComparator(
        order,
        products.all.obj,
        filters.sorting.orderBy.toLowerCase(),
      ),
    [filters.sorting.orderBy, products.all.obj],
  );

  const sortByNumber = useCallback(
    (order) =>
      createNumberComparator(
        order,
        products.all.obj,
        filters.sorting.orderBy.toLowerCase(),
      ),
    [filters.sorting.orderBy, products.all.obj],
  );

  const sortByOrders = useCallback(
    (order) => createOrdersComparator(order, products.sortedByOrders),
    [products.sortedByOrders],
  );

  const sortProducts = useCallback(
    (allProductIds) => {
      const order = filters.sorting.order;
      const orderBy = filters.sorting.orderBy.toLowerCase();
      const { productIds, prescribedProductIds } = separatePrescribedProducts(
        allProductIds,
        products.all.obj,
      );

      if (orderBy === 'name') {
        return productIds
          .sort(sortByName(order))
          .concat(prescribedProductIds.sort(sortByName(order)));
      }
      if (orderBy === 'stock number') {
        return productIds
          .sort(sortByName(order))
          .concat(prescribedProductIds.sort(sortByName(order)));
      }
      if (orderBy === 'date') {
        return productIds
          .sort(sortByDate(order))
          .concat(prescribedProductIds.sort(sortByDate(order)));
      }
      if (orderBy === 'orders') {
        return productIds
          .sort(sortByOrders(order))
          .concat(prescribedProductIds.sort(sortByOrders(order)));
      }
      if (orderBy === 'rating') {
        return productIds
          .sort(sortByNumber(order))
          .concat(prescribedProductIds.sort(sortByNumber(order)));
      }
      return productIds;
    },
    [
      filters.sorting.order,
      filters.sorting.orderBy,
      products.all.obj,
      sortByName,
      sortByDate,
      sortByOrders,
      sortByNumber,
    ],
  );

  const applyAllFilters = useCallback(() => {
    const arr = (products.all.ids || [])?.filter((id) => {
      // Apply search value
      const product = (products.all.obj || {})?.[id];
      let isSearchMatch, isFiltersMatch;
      isFiltersMatch = applyFilters(product);
      isSearchMatch = applySearch(product);
      // if start date exists it should be before current date
      const isStartDateValid =
        !product.start_date ||
        isAfter(new Date(), new Date(product.start_date));
      return isSearchMatch && isFiltersMatch && isStartDateValid;
    });
    return sortProducts(arr);
  }, [
    applyFilters,
    applySearch,
    products.all.ids,
    products.all.obj,
    sortProducts,
  ]);

  useEffect(() => {
    if (!initialFiltersApplied) {
      return undefined;
    }
    const timeoutId = setTimeout(() => {
      dispatch(ApplyFilters(applyAllFilters()));
      dispatch(SetLoading(false));
    }, APPLY_FILTERS_DELAY_MS);
    return () => clearTimeout(timeoutId);
  }, [applyAllFilters, dispatch, initialFiltersApplied]);

  useEffect(() => {
    if (initialFiltersApplied || !products?.all?.ids?.length) {
      return;
    }
    setInitialFiltersApplied(true);
    dispatch(ApplyFilters(applyAllFilters()));
  }, [
    applyAllFilters,
    dispatch,
    initialFiltersApplied,
    products?.all?.ids?.length,
  ]);

  const updateShownProducts = useCallback(async () => {
    dispatch(UpdateProductsPage());
  }, [dispatch]);

  const onChangeFilter = useCallback(
    (newFilter) => {
      dispatch(SetLoading(true));
      window.scroll(0, 0);
      dispatch(ChangeFilter(newFilter));
    },
    [dispatch],
  );

  const onMultipleSelectChange = useCallback(
    (name) => (event) => {
      const {
        target: { value },
      } = event;
      const checkedValues =
        typeof value === 'string' ? value.split(',') : value;

      if (name === 'available') {
        const checkedValue = checkedValues[checkedValues.length - 1];

        onChangeFilter({
          [name]: filters[name].map((item) => {
            return item.title === checkedValue
              ? { ...item, checked: true }
              : { ...item, checked: false };
          }),
        });
      } else {
        onChangeFilter({
          [name]: filters[name].map((item) =>
            checkedValues.includes(item.title)
              ? { ...item, checked: true }
              : { ...item, checked: false },
          ),
        });
      }
    },
    [filters, onChangeFilter],
  );

  const applyMobileFilters = useCallback(
    (filters) => {
      dispatch(SetLoading(true));
      dispatch(SetFilters(filters));
    },
    [dispatch],
  );

  const onRemoveFilter = useCallback(
    (filterTitle, title) => () => {
      onChangeFilter({
        [filterTitle]: filters[filterTitle].map((item) =>
          item.title === title ? { ...item, checked: false } : item,
        ),
      });
    },
    [filters, onChangeFilter],
  );

  const onClearAllFilters = useCallback(() => {
    dispatch(SetLoading(true));
    dispatch(ClearFilters());
  }, [dispatch]);

  const onChangeSorting = useCallback(
    (orderBy, order) => () => {
      dispatch(SetLoading(true));
      const newFilter = { ...filters.sorting };
      if (orderBy) {
        newFilter.orderBy = orderBy.toLowerCase();
      }
      if (order) {
        newFilter.order = order === 'desc' ? 'asc' : 'desc';
      }
      dispatch(ChangeFilter({ sorting: newFilter }));
    },
    [filters, dispatch],
  );

  const getDependentFilterOptions = useCallback(
    (currentFilterId, currentFilters) =>
      getDependentFilterOptionsPure(currentFilterId, currentFilters, {
        productIds: products.all.ids || [],
        productsObj: products.all.obj,
        filters,
      }),
    [filters, products.all.ids, products.all.obj],
  );

  const getDependentFilters = useCallback(
    (filters) => getDependentFiltersPure(filters, getDependentFilterOptions),
    [getDependentFilterOptions],
  );

  const dependentFilters = getDependentFilters(filters);

  return {
    products,
    updateShownProducts,
    onChangeFilter,
    filters,
    onMultipleSelectChange,
    applyMobileFilters,
    onRemoveFilter,
    onClearAllFilters,
    isLoading,
    onChangeSorting,
    applyFilters,
    applyAllFilters,
    sortProducts,
    sortByNumber,
    sortByDate,
    sortByName,
    getDependentFilters,
    dependentFilters,
  };
};

export default useFilters;
